import { useState } from "react";
import { Link } from "react-router-dom";
import { CONTACT_SOCIAL } from "../data/contact";
import { FOOTER_INFO_LINKS, FOOTER_QUICK_LINKS } from "../lib/navConfig";
import { apiFetchPublic, ApiError } from "../lib/api";

function SocialIconInstagram() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="20" height="20" x="2" y="2" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function SocialIconYouTube() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 17a24.1 24.1 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.6 49.6 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.1 24.1 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.6 49.6 0 0 1-16.2 0A2 2 0 0 1 2.5 17z" />
      <polygon points="10,15 15,12 10,9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SocialIconFacebook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function SocialIconLinkedIn() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function NewsletterArrow() {
  return (
    <svg width="36" height="8" viewBox="0 0 40 8" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <line x1="0" y1="4" x2="38" y2="4" />
      <path d="M34 1 L39 4 L34 7" />
    </svg>
  );
}

const SOCIAL_ICONS = {
  instagram: SocialIconInstagram,
  youtube: SocialIconYouTube,
  facebook: SocialIconFacebook,
  linkedin: SocialIconLinkedIn,
};

/**
 * @param {{ current?: string }} props
 */
export default function SiteFooter({ current = "home" }) {
  const linkClass = (key) =>
    `site-footer__link${key === current ? " site-footer__link--active" : ""}`;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackTone, setFeedbackTone] = useState(/** @type {"success" | "error"} */ ("success"));

  async function handleNewsletterSubmit(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || !trimmedEmail) {
      setFeedbackTone("error");
      setFeedback("Add your name and email to join the list.");
      return;
    }

    setSubmitting(true);
    setFeedback("");

    try {
      const body = await apiFetchPublic("/newsletter/subscribe", {
        method: "POST",
        body: JSON.stringify({ name: trimmedName, email: trimmedEmail }),
      });
      setFeedbackTone("success");
      setFeedback(body?.message || "Thanks — you're on the list.");
      setName("");
      setEmail("");
    } catch (err) {
      setFeedbackTone("error");
      setFeedback(err instanceof ApiError ? err.message : "Something went wrong — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__rule" />

        <div className="site-footer__grid">
          <div className="site-footer__brand-col">
            <img
              src={`${process.env.PUBLIC_URL}/uploads/cj-mark.svg`}
              alt="CJ.PHOTOS"
              className="site-footer__logo"
            />
            <p className="site-footer__tagline">
              Photographer. Storyteller. Explorer. Documenting the world and the
              journeys that shape it.
            </p>
            <div className="site-footer__social">
              {CONTACT_SOCIAL.map((social) => {
                const Icon = SOCIAL_ICONS[social.key];
                return (
                  <a
                    key={social.key}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="site-footer__col">
            <div className="site-footer__heading">QUICK LINKS</div>
            <div className="site-footer__links">
              {FOOTER_QUICK_LINKS.map((item) => (
                <Link key={item.key} to={item.href} className={linkClass(item.key)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="site-footer__col">
            <div className="site-footer__heading">INFO</div>
            <div className="site-footer__links">
              {FOOTER_INFO_LINKS.map((item) => (
                <Link key={item.key} to={item.href} className={linkClass(item.key)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="site-footer__newsletter-col">
            <div className="site-footer__heading">STAY IN TOUCH</div>
            <p className="site-footer__newsletter-copy">
              Get new stories, updates and adventures delivered to your inbox.
            </p>
            <form className="site-footer__newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="site-footer__newsletter">
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  aria-label="Your name"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="site-footer__newsletter">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  aria-label="Your email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={submitting}
                />
                <button
                  type="submit"
                  className="site-footer__newsletter-submit"
                  aria-label="Subscribe"
                  disabled={submitting}
                >
                  <NewsletterArrow />
                </button>
              </div>
              {feedback ? (
                <p
                  className={`site-footer__newsletter-feedback site-footer__newsletter-feedback--${feedbackTone}`}
                  role="status"
                  aria-live="polite"
                >
                  {feedback}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        <div className="site-footer__bar">
          <span>© 2024 CJ Jayamaha</span>
          <span>All Rights Reserved.</span>
        </div>
      </div>
    </footer>
  );
}
